/**
 * caregaps.ts — response-SHAPE gate schemas for the Care Gaps surface (Screen D). These feed
 * fetchEnvelope's optional `schema` param so STRUCTURAL render-shape drift (array vs non-array, a
 * missing REQUIRED field, a wrong PRIMITIVE type) is caught at the API boundary instead of throwing
 * deep in the grid/KPI render. They are NOT value validators — see base.ts LENIENCY CONVENTIONS.
 *
 * Two endpoints back this page (see client/pages/CareGaps.tsx):
 *   - GET /api/v1/care-gaps/worklist/stats  -> CareGapWorklistSummary   (usePage<CareGapWorklistSummary>)
 *   - GET /api/v1/care-gaps/worklist?<q>    -> CareGapWorklistRow[]      (fetchEnvelope<CareGapWorklistRow[]>)
 *
 * Every field mirrors the EXACT optionality of shared/contracts.ts:
 *   `| null` => `.nullable()`; primitives are the wide z.string()/z.number()/z.boolean(); objects allow
 *   extra keys (no `.strict()`); no enum/literal value-policing. Each schema carries an AssertExtends
 *   guard proving the CONTRACT type is assignable to the schema's inferred type (never stricter).
 */
import { z } from "zod";
import type {
  CareGapWorklistRow,
  CareGapWorklistStat,
  CareGapWorklistSummary,
} from "@shared/contracts";
import { provenancedRowShape, type AssertExtends } from "./base";

/**
 * CareGapWorklistStat (per-measure rollup row in the summary). Extends ProvenancedRow via the shared
 * shape fragment; measure_name/domain are required-but-nullable; the three metrics are plain numbers.
 */
export const careGapWorklistStatSchema = z.object({
  ...provenancedRowShape,
  measure_name: z.string().nullable(),
  domain: z.string().nullable(),
  open_gaps: z.number(),
  denominator: z.number(),
  closure_rate: z.number(),
});
type _StatLink = AssertExtends<CareGapWorklistStat, z.infer<typeof careGapWorklistStatSchema>>;

/**
 * CareGapWorklistSummary — KPI headline numbers + per-measure rollup + the honest pediatric-excluded
 * count. `byMeasure` is the array whose non-array drift the page already guards against; the gate makes
 * that drift a boundary rejection (routes to the recovery UI) rather than a silent empty grid.
 */
export const careGapWorklistSummarySchema = z.object({
  byMeasure: z.array(careGapWorklistStatSchema),
  totalOpen: z.number(),
  membersWithGaps: z.number(),
  excludedPediatric: z.number(),
  topDomain: z.string().nullable(),
});
type _SummaryLink = AssertExtends<CareGapWorklistSummary, z.infer<typeof careGapWorklistSummarySchema>>;

/**
 * CareGapWorklistRow — one open care gap (member x measure x period). Extends ProvenancedRow; the
 * identity fields (score_key / enterprise_member_key) are required strings, the descriptive labels are
 * required-but-nullable, and the denominator/numerator flags are booleans.
 */
export const careGapWorklistRowSchema = z.object({
  ...provenancedRowShape,
  score_key: z.string(),
  enterprise_member_key: z.string(),
  last_name: z.string().nullable(),
  first_name: z.string().nullable(),
  measure_name: z.string().nullable(),
  domain: z.string().nullable(),
  system_gap_reason: z.string().nullable(),
  in_denominator: z.boolean(),
  in_numerator: z.boolean(),
});
type _RowLink = AssertExtends<CareGapWorklistRow, z.infer<typeof careGapWorklistRowSchema>>;

/** Worklist endpoint returns a bare array of rows (the envelope's `data` is the array itself). */
export const careGapWorklistSchema = z.array(careGapWorklistRowSchema);
